import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { SafeHtml } from "@angular/platform-browser";
import {
    DisplayMode,
    GridColumnViewSettings,
    GridComponent,
    IGridColumn,
    IGridSettings,
    IPagedData,
    ITableSettings
} from "@features/grid";
import { IQueryCommand } from "@features/filter";
import { WidgetGridDataService } from "../api/widget-grid-data.service";
import { WidgetGridViewService } from "../api/widget-grid-view.service";
import {
    IAggregatedValues,
    IColumnTemplate,
    IGridDisplayView,
    IGridDisplayViewColumn,
    IGridPdfTemplateSettings,
    IPagedGridSettings,
    IQueryPageRequest
} from "../widget-grid.models";
import { IVariableEmitter } from "../../core/variables/variable-emitter";
import { IVariableReceiver } from "../../core/variables/variable-receiver";
import { VariableHubService } from "../../core/variables/variable-hub.service";
import {
    IEmittedVariable,
    IQueryVariables,
    IStaticVariable,
    IVariableRule
} from "../../core/variables/variable-models";
import { VariableRuleService } from "../../core/variables/variable-rule.service";
import { WidgetGridXlsExportingService } from "@main/reporting.v3/widget.grid/api/widget-grid-xls-exporting.service";
import {
    IQueryAggregationsRequest,
    IQueryColumnAggregation,
    PdfExportSettings,
    PdfExportSource,
    WidgetOutputMode
} from "@main/reporting.v3/core/reporting-models";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { WidgetGridPdfExportingService } from "@main/reporting.v3/widget.grid/api/widget-grid-pdf-exporting.service";
import { WidgetGridAuxiliaryService } from "@main/reporting.v3/widget.grid/api/widget-grid-auxiliary.service";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import { IPdfExportable } from "@main/reporting.v3/core/exporting/pdf-exportable";
import { IXlsExportable } from "@main/reporting.v3/core/exporting/xls-exportable";
import { getDisplayModeByName } from "@features/grid/enums/display-mode";
import { UserHelperService } from "@main/users/user-helper.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { PlayerService } from "@project/player/player.service";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { BehaviorSubject, firstValueFrom, skip } from "rxjs";
import { AccountStatus, PatientLogin } from "@main/users";
import { MessageService } from "primeng/api";
import { IHash } from "@bbwt/interfaces";
import { Message } from "@bbwt/classes";
import { v4 as uuidv4 } from "uuid";


@Component({
    selector: "widget-grid",
    templateUrl: "./widget-grid.component.html",
    styleUrls: ["./widget-grid.component.scss", "../../styles/html-code-container.scss"]
})
export class WidgetGridComponent implements IWidgetComponent, IVariableEmitter, IVariableReceiver, IPdfExportable, IXlsExportable, OnDestroy {
    // Widget settings.
    public readonly widgetType: string = "table";
    public readonly widgetSelector: string = "widget-grid";

    // Variable handling settings.
    public readonly variableEmitterId: string = uuidv4();
    public readonly variableReceiverId: string = uuidv4();

    // Grid data fields.
    protected queryCommand: IQueryCommand;
    protected gridPagedData: IPagedData<any>;
    protected columnTemplates: IColumnTemplate[];
    protected aggregatedValues: IAggregatedValues;

    // Auxiliary settings.
    protected footerVisible: boolean = false;
    protected gridFieldToQueryAliasMap: IHash<string> = {};

    // Grid settings.
    protected gridSettings: IGridSettings;
    protected tableSettings: ITableSettings;

    // Pdf view settings.
    protected renderPdfViewHidden: boolean;

    // Loading settings.
    public gridDataLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public pdfExportingLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public xlsExportingLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);

    // Component fields.
    public widgetTitle: string;
    public hasPatientId: boolean;
    public onPatientBeforeDirect: PatientLogin;
    public widgetLinks: { id: string; name: string; url: string }[] = [];

    private variables: IEmittedVariable[];
    private queryVariables: IEmittedVariable[];

    private _widgetSourceId: string;
    private _widgetVisible: boolean;
    private _gridDisplayView: IGridDisplayView;
    private _staticVariables: IStaticVariable[];
    private _pdfViewTable: ElementRef<HTMLElement>;

    // Shows or hides exporting button.
    @Input() exportable: boolean = true;

    // Determines the height of the grid.
    @Input() scrollHeight: string = "70vh";

    // Determines which html code should be used for rendering: web view of pdf exporting view.
    @Input() output: WidgetOutputMode = "web";

    // Determines if grid should ignore its own displaying rules.
    @Input() ignoreDisplayRule: boolean = false;

    // Emitter to notify that pdf view is ready for exporting.
    @Output() readonly pdfExportingReady: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild(GridComponent, { static: false }) protected grid: GridComponent;

    constructor(private widgetGridPdfExportingService: WidgetGridPdfExportingService,
                private widgetGridXlsExportingService: WidgetGridXlsExportingService,
                private widgetGridAuxiliaryService: WidgetGridAuxiliaryService,
                private widgetGridViewService: WidgetGridViewService,
                private widgetGridDataService: WidgetGridDataService,
                private variableRuleService: VariableRuleService,
                private variableHubService: VariableHubService,
                private userHelperService: UserHelperService,
                private patientService: PatientService,
                private messageService: MessageService,
                private playerService: PlayerService,
                private elementRef: ElementRef,
                broadcastService: BroadcastService) {
        // TODO: This logic may no longer be necessary, as patient switch now emits
        //  patient_id as variable when selected patient changes. This widget will
        //  capture this variable in receiveEmittedVariables and refresh accordingly.
        //  Retained pending verification: this check uses PatientId, while the variable
        //  is commonly patient_id, so it may not detect the patient variable.
        broadcastService
            .on(PatientService.CurrentPatientChangedEventName)
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                // reload only if this grid is not already refreshed by new variable flow
                if (!this.gridDisplayView?.queryVariables?.includes("PatientId")) return;
                this.grid?.reload();
            });
    }

    // Captures pdf view container and notify when it is ready.
    @ViewChild("pdfViewTable") protected set pdfViewTable(value: ElementRef<HTMLElement>) {
        if (!!value) {
            // Wait a little time for the table to render.
            setTimeout((): void => {
                this._pdfViewTable = value;
                this.pdfExportingReady.emit();
            }, 100);
        } else this._pdfViewTable = null;
    }

    protected get pdfViewTable(): ElementRef<HTMLElement> {
        return this._pdfViewTable;
    }

    // Determines if grid is able to emit variables.
    @Input() set variableEmitter(value: boolean) {
        if (!!value || value == null) this.variableHubService.registerVariableEmitter(this);
        else this.variableHubService.unregisterVariableEmitter(this);
    }

    // Determines if grid is able to receive variables.
    @Input() set variableReceiver(value: boolean) {
        if (!!value || value == null) this.variableHubService.registerVariableReceiver(this);
        else this.variableHubService.unregisterVariableReceiver(this);
    }

    // Set of fixed-value variables to consider during data fetching.
    @Input() set staticVariables(value: IStaticVariable[]) {
        if (!value) return;
        this._staticVariables = value;
        this.refreshWidgetView();
    }

    get staticVariables(): IStaticVariable[] {
        return this._staticVariables;
    }

    // View of the grid. Refresh grid settings every time this variable changes.
    @Input() set gridDisplayView(value: IGridDisplayView) {
        if (!value) return;
        this._gridDisplayView = value;
        this._widgetSourceId = value.widgetSourceId;
        this.refreshWidgetView(true);
    };

    get gridDisplayView(): IGridDisplayView {
        return this._gridDisplayView;
    }

    // Widget source of the grid. Refresh all widget every time this variable changes.
    @Input() set widgetSourceId(value: string) {
        if (this.widgetSourceId === value) return;
        this._widgetSourceId = value;
        this.refreshWidget().then();
    }

    get widgetSourceId(): string {
        return this._widgetSourceId;
    }

    @Input() set widgetVisible(value: boolean) {
        this._widgetVisible = value;
    }

    get widgetVisible(): boolean {
        return this.ignoreDisplayRule || this._widgetVisible;
    }

    get widgetName(): string {
        return this.gridDisplayView?.widgetSource?.name;
    }

    get widgetCode(): string {
        return this.gridDisplayView?.widgetSource?.code;
    }

    get pdfViewVisible(): boolean {
        return this.widgetVisible;
    }

    get xlsViewVisible(): boolean {
        return this.widgetVisible;
    }

    get gridTableClass(): string {
        return this.widgetGridAuxiliaryService.gridTableClass;
    }

    get widgetContainerClass(): string {
        return this.widgetGridAuxiliaryService.widgetContainerClass;
    }


    public ngOnDestroy(): void {
        this.variableHubService.unregisterVariableReceiver(this);
        this.variableHubService.unregisterVariableEmitter(this);
        this.pdfExportingLoading.complete();
        this.xlsExportingLoading.complete();
        this.gridDataLoading.complete();
    }

    // Variable management methods.
    public receiveEmittedVariables(variables: IEmittedVariable[]): void {
        // Save current state of variables.
        const oldVariables: IEmittedVariable[] = this.variables ?? [];
        this.variables = variables;

        // If there is no widget yet, take all variables as last emitted variables.
        // Otherwise, take as last emitted variables only the variables related to this widget.
        const oldQueryVariables: IEmittedVariable[] = this.queryVariables ?? [];
        this.queryVariables = this.variableRuleService.filterVariableSet(this.gridDisplayView?.queryVariables, variables);

        if (!this.gridDisplayView) return;

        // Show/hide widget if required.
        const displayRule: IVariableRule = this.gridDisplayView.widgetSource?.displayRule;

        this.widgetVisible = !displayRule || this.variableRuleService.containsMatch(displayRule, variables);

        // Refresh the widget title if some related variable changed its value.
        if (!this.variableRuleService.equalVariableSets(this.variables, oldVariables)) this.refreshTitle();

        // Refresh the widget content if some related variable changed its value.
        if (!this.variableRuleService.equalVariableSets(this.queryVariables, oldQueryVariables)) this.reloadGrid().then();
    }

    // Refreshing methods.
    private async refreshWidget(): Promise<void> {
        if (!this.widgetSourceId) return;

        const view = await this.widgetGridViewService.getDisplayView(this.widgetSourceId);

        // TODO: Represent the Actions column separately from database query columns.
        // Features that use query columns must explicitly exclude it, including PDF exporting
        // and the PDF view template.
        if (!view.columns.some(c => c.queryAlias.endsWith(".ActionMenu"))) {
            const prefix = view.querySourceId.split(".")[0];
            const actionCol: IGridDisplayViewColumn = {
                id: `${prefix}.ActionMenu`,
                queryAlias: `${prefix}.ActionMenu`,
                header: "Actions",
                dataType: "string",
                inputType: "text",
                visible: true,
                sortable: false,
                displayMode: DisplayMode.Text,
                extraSettings: { actionItems: [] },
                footer: {},
                sortOrder: view.columns.length,
                variableName: null
            };
            view.columns.push(actionCol);
        }

        this.gridDisplayView = view;

        this.loadLinks().then();
    }

    private refreshGridSettings(): void {
        if (!this.gridDisplayView) return;

        this.widgetVisible = this.ignoreDisplayRule || !this.gridDisplayView.widgetSource?.displayRule;
        this.footerVisible = this.gridDisplayView.columns.some(column => column.footer?.expressions?.length);

        // TODO: Detect patient-id columns independently of their query alias.
        // A user-defined alias without the patientId suffix prevents detection.
        this.hasPatientId = this.gridDisplayView.columns.some((col) =>
            col.queryAlias.toLowerCase().endsWith("patientid"));

        this.tableSettings = {
            autoLayout: true,
            scrollable: true,
            scrollHeight: this.scrollHeight ?? "70vh",
            sortField: this.gridDisplayView.defaultSortColumnAlias?.replace(".", "_"),
            sortOrder: this.gridDisplayView.defaultSortOrder,
            styleClass: "p-datatable-gridlines p-datatable-striped",
            selectionMode: this.gridDisplayView.isRowSelectable || this.hasPatientId ? "single" : null,
            dataKey: null,
            lazy: true
        };

        this.createGridColumnSettings(this.gridDisplayView.columns);

        this.gridSettings = {
            readonly: true,
            dataService: this,
            exportEnabled: false,
            dataServiceGetPageMethodName: this.requestData.name,
            visibleColumnsSelector: this.gridDisplayView.showVisibleColumnsSelector
        };

        // If this is not the web view, then the BBWT3 grid is not rendered,
        // so the data is not fetched automatically. It needs to be requested manually.
        if (this.output !== "web") this.requestData().then();

        this.onGridRowUnselect();
    }

    private refreshTitle(): void {
        if (!this.gridDisplayView?.widgetSource?.title) return;
        let processedTitle = this.variableRuleService.embedVariableValues(this.gridDisplayView.widgetSource?.title, this.variables);
        this.widgetTitle = this.variableRuleService.cleanupUnmatchedVariables(processedTitle);
    }

    private refreshWidgetView(complete: boolean = false): void {
        if (complete) {
            this.refreshGridSettings();
        } else {
            void this.reloadGrid();
        }
        this.refreshTitle();
    }

    private async requestData(queryCommand?: IQueryCommand): Promise<IPagedData<any>> {
        this.gridDataLoading.next(true);

        try {
            // If no query command is specified (e.g, data requesting in PDF preview) use a default one.
            this.queryCommand = queryCommand ?? {
                skip: 0,
                take: 10,
                sortingField: this.tableSettings?.sortField,
                sortingDirection: this.tableSettings?.sortOrder
            };

            const querySourceId: string = this.gridDisplayView.querySourceId;
            const queryVariables: IQueryVariables = { variables: this.getWidgetVariables() };
            const queryTotalRows: number = await this.widgetGridDataService.getDataRowsCount(this.gridDisplayView.querySourceId, queryVariables);

            if (this.footerVisible) this.aggregatedValues = await this.requestFooterData(queryVariables);

            const queryPageRequest: IQueryPageRequest = {
                gridSettings: this.getPagedGridSettingsFromQueryCommand(this.queryCommand),
                queryVariables: queryVariables
            };

            const queryData: any[] = await this.widgetGridDataService.getDataRows(querySourceId, queryPageRequest);

            this.gridPagedData = <IPagedData<any>>{
                items: this.widgetGridAuxiliaryService.replaceDotsInFieldNames(queryData),
                total: queryTotalRows
            };

        } finally {
            this.gridDataLoading.next(false);
        }

        return this.gridPagedData;
    }

    private async requestFooterData(queryVariables: IQueryVariables): Promise<IAggregatedValues> {
        const querySourceId: string = this.gridDisplayView.querySourceId;

        const aggregations: IQueryColumnAggregation[] = this.gridDisplayView.columns
            .filter(column => !!column.footer?.expressions?.length)
            .map(column => <IQueryColumnAggregation>{
                expressions: column.footer.expressions,
                queryAlias: column.queryAlias
            });

        const queryAggregationsRequest: IQueryAggregationsRequest = {
            queryVariables: queryVariables,
            aggregations: aggregations,
        }

        const queryAggregatedValues: any = await this.widgetGridDataService.getAggregations(querySourceId, queryAggregationsRequest);

        return this.widgetGridAuxiliaryService.createFooterOutput(this.gridDisplayView.columns, queryAggregatedValues);
    }

    private async reloadGrid(): Promise<void> {
        if (!this.gridDisplayView) return;

        // If this widget is in web view, rely on in the BBWT3 grid to reload the data.
        if (this.output === "web") {
            if (!this.grid) return;

            // If grid is currently loading, await for loading to end.
            if (this.gridDataLoading.value) await firstValueFrom(this.gridDataLoading.pipe(skip(1)));

            // Reset grid selection.
            this.grid.selection = null;
            this.grid._table.selection = null;
            this.onGridRowUnselect();

            // Reload the grid.
            await this.grid.reload();

            // Otherwise, re-fetch the data manually.
        } else await this.requestData(this.queryCommand);
    }

    // Auxiliary methods.
    private getWidgetVariables(): IEmittedVariable[] {
        const staticVariables: IEmittedVariable[] = this.staticVariables?.map(variable =>
            <IEmittedVariable>{ ...variable, empty: false, behaviorOnEmpty: "populate" }) ?? [];

        return staticVariables.concat(this.queryVariables ?? []);
    }

    private getExportingQueryPageRequest(): IQueryPageRequest {
        const queryVariables: IQueryVariables = { variables: this.getWidgetVariables() };
        return { gridSettings: { take: 100_000 }, queryVariables: queryVariables };
    }

    private createGridColumnSettings(columns: IGridDisplayViewColumn[]): void {
        this.columnTemplates = [];
        this.tableSettings.columns = [];
        this.gridFieldToQueryAliasMap = {};

        columns.forEach(column => {
            if (!column.visible) return;

            const gridField: string = column.queryAlias.replace(".", "_");
            this.gridFieldToQueryAliasMap[gridField] = column.queryAlias;

            const defaultDateFormat: string = !!column.extraSettings.dateMomentFormat?.length
                ? column.extraSettings.dateMomentFormat
                : "YYYY-MM-DDTHH:mm:ss";

            // This patch is necessary because the display mode enum of MiPro (number)
            // is different from the display mode enum of BBWT (string).
            if (column.displayMode) {
                if (!Object.values(DisplayMode).includes(column.displayMode)) {
                    column.displayMode = getDisplayModeByName(column.displayMode.toString());
                }
            }

            const gridColumn: IGridColumn = {
                field: gridField,
                header: column.header,
                sortable: column.sortable,
                displayMode: column.displayMode,
                displayDateMomentFormat: defaultDateFormat,
                displayConditionalTrueValue: column.extraSettings.trueValueLabel ?? "true",
                displayConditionalFalseValue: column.extraSettings.falseValueLabel ?? "false",
                countNullAsFalse: column.extraSettings.countNullAsFalse,
                decimalPlaces: column.extraSettings.decimalPlaces
            };
            gridColumn.displayHandler = this.widgetGridAuxiliaryService.getDisplayHandler(column, gridColumn);

            if (!!column.extraSettings.width || !!column.extraSettings.minWidth || !!column.extraSettings.maxWidth) {
                gridColumn.viewSettings = new GridColumnViewSettings({
                    width: column.extraSettings.width,
                    minWidth: column.extraSettings.minWidth,
                    maxWidth: column.extraSettings.maxWidth,
                });
            }

            const columnTemplate: IColumnTemplate = {
                column: gridColumn,
                customTemplate: column.extraSettings.customFormat,
                originalColumn: column
            };

            this.columnTemplates.push(columnTemplate);

            this.tableSettings.columns.push(gridColumn);
        });
    }

    private getPagedGridSettingsFromQueryCommand(queryCommand: IQueryCommand): IPagedGridSettings {
        if (!queryCommand) return {};
        return <IPagedGridSettings>{
            skip: queryCommand.skip,
            take: queryCommand.take,
            sortingDirection: queryCommand.sortingDirection,
            sortingField: this.gridFieldToQueryAliasMap[queryCommand.sortingField]
        };
    }

    protected getCustomFormattedCellHtml(template: IColumnTemplate, rowData: any): SafeHtml {
        return this.widgetGridAuxiliaryService.getCustomFormattedCellHtml(this.gridDisplayView.columns, template, rowData);
    }

    protected getColumnStyles(column: IGridColumn): object {
        return column.viewSettings ? column.viewSettings.getStyles() : {};
    }

    protected isQueryColumn(column: IColumnTemplate): boolean {
        // TODO: This should not be necessary. The Actions Menu logic creates a false column for this
        //  feature but treats it like any other query column, so the only way to tell a query
        //  column from the Actions column is by its field.
        return !column.originalColumn?.queryAlias.endsWith(".ActionMenu");
    }

    protected getQueryColumnTemplates(): IColumnTemplate[] {
        // TODO: This should not be necessary either. When the Action Menu logic no longer
        //  uses a false column, remove both methods and their usages.
        return this.columnTemplates.filter(column => this.isQueryColumn(column));
    }

    protected async onGridRowSelect(rowSelEvent: { data: any }): Promise<void> {
        if (!this.gridDisplayView?.isRowSelectable && !this.hasPatientId) return;

        const patientVariables: IEmittedVariable[] = await this.getPatientVariables(rowSelEvent);

        const gridVariables: IEmittedVariable[] = this.getGridVariables(rowSelEvent);

        const variables: IEmittedVariable[] = [...patientVariables, ...gridVariables];

        this.variableHubService.emitVariables(this.variableEmitterId, variables, this.variableReceiverId);
    }

    private getGridVariables(rowSelEvent: { data: any }): IEmittedVariable[] {
        if (!this.gridDisplayView?.isRowSelectable) return [];

        return this.gridDisplayView.columns
            .filter(column => !!column.variableName)
            .map(column => <IEmittedVariable>{
                name: column.variableName,
                value: rowSelEvent.data[column.queryAlias.replace(".", "_")],
                behaviorOnEmpty: "clean",
                $type: this.widgetGridAuxiliaryService.getVariableType(column),
            });
    }

    private async getPatientVariables(rowSelEvent: { data: any }): Promise<IEmittedVariable[]> {
        if (!this.hasPatientId) return [];

        // TODO: Rework the patient id detection. It does not work when the patient id
        //  column is aliased to a name not ending in the patientId suffix.
        const row = rowSelEvent.data;

        const patientIdKey = Object.keys(row).find(k =>
            k.toLowerCase().endsWith("patientid")
        );
        const id = patientIdKey ? row[patientIdKey] : null;

        if (!patientIdKey) {
            const vars: IEmittedVariable[] = this.gridDisplayView.columns
                .filter(col => !!col.variableName)
                .map(col => <IEmittedVariable>{
                    name: col.variableName,
                    value: row[col.queryAlias.replace(".", "_")],
                    $type: this.widgetGridAuxiliaryService.getVariableType(col),
                    behaviorOnEmpty: "clean"
                });
            this.variableHubService.emitVariables(this.variableEmitterId, vars, this.variableReceiverId);

            return [];
        }

        if (id == null || id === "") {
            this.messageService.add(Message.Info("No Patient ID is available for the selected row. Please select a different entry."));

            return [];
        }

        let patientData: PatientLogin;
        const allowedStatuses = [
            AccountStatus.None,
            AccountStatus.Active,
            AccountStatus.Invited
        ];

        try {
            patientData = await this.playerService.getFromPatientId(id);
        } catch {
            this.messageService.add(Message.Info("You do not have permission to view this patient from the current organization."));

            return [];
        }

        if (patientData.isArchived || !allowedStatuses.includes(patientData.accountStatus)) {
            this.messageService.add(Message.Info("This patient is not active. Only active patients can be selected"));

            return [];
        }

        const userOrgId = this.userHelperService.organizationId;
        const isSuperadmin = this.userHelperService.isSuperAdmin;
        const isMiProAdmin = this.userHelperService.isMiProAdmin;

        if (!isSuperadmin && !isMiProAdmin) {
            const hasConsent = patientData?.sharedOrganizations?.some(org => {
                const orgIdPrefix = org.id.split("-")[0];
                return orgIdPrefix === userOrgId;
            });

            if (!hasConsent) {
                this.messageService.add(Message.Info("You do not have permission to view this patient."));
                return [];
            }
        }

        // The Three dot Action button doesn't redirect the patient instantly until the user selects an option:
        if ((rowSelEvent as any).type != "row") {
            this.onPatientBeforeDirect = patientData;
            return [];
        }

        const fullName = [patientData.firstName, patientData.lastName]
            .filter(Boolean)
            .join(" ");

        this.patientService.currentPatientPlaceHolder = { id, fullName };

        const emittedPatientVar: IEmittedVariable = {
            name: "PatientId",
            value: id,
            $type: "number",
            empty: false,
            behaviorOnEmpty: "clean"
        };

        // TODO: This is actually doing nothing, this is being treated as an emitted variable and not
        //  as the context variable @patient_id. When this variable reach the backend, it is discarded
        //  as its name doesn't match any existing variable (to match, the variable in question have
        //  to be declared in the query as #@PatientId, and that's simply not valid at all).
        const legacyPatientVar: IEmittedVariable = {
            name: "@PatientId",
            value: id,
            $type: "number",
            empty: false,
            behaviorOnEmpty: "clean"
        };

        return [emittedPatientVar, legacyPatientVar]
    }

    protected onGridRowUnselect(): void {
        if (!this.gridDisplayView?.isRowSelectable) return;
        const variables: IEmittedVariable[] = this.gridDisplayView.columns
            .filter(column => !!column.variableName)
            .map(column => <IEmittedVariable>{
                name: column.variableName,
                value: null, empty: true,
                behaviorOnEmpty: "clean",
                $type: this.widgetGridAuxiliaryService.getVariableType(column),
            });

        this.variableHubService.emitVariables(this.variableEmitterId, variables, this.variableReceiverId);
    }

    // PDF exporting methods.
    private async renderPdfViewForFunction<T>(func: () => Promise<T>): Promise<T> {
        if (!!this.pdfViewTable) return func();

        // Render pdf view hidden, and then execute parameter function.
        this.renderPdfViewHidden = true;
        await firstValueFrom(this.pdfExportingReady);

        return func().finally((): boolean => this.renderPdfViewHidden = false);
    }

    private getGridPdfTemplateSettings(): IGridPdfTemplateSettings {
        return {
            template: this.elementRef?.nativeElement?.innerHTML,
            gridTableTemplate: this.pdfViewTable?.nativeElement?.outerHTML,
        };
    }

    public async getPdfConfiguration(): Promise<JsReportConfiguration> {
        if (!this.pdfViewTable) return this.renderPdfViewForFunction(this.getPdfConfiguration.bind(this));

        const settings: IGridPdfTemplateSettings = this.getGridPdfTemplateSettings();
        return await this.widgetGridPdfExportingService.getPdfConfiguration(settings);
    }

    public async generatePdf(): Promise<void> {
        if (await firstValueFrom(this.pdfExportingLoading)) return;

        try {
            this.pdfExportingLoading.next(true);

            const configuration: JsReportConfiguration = await this.getPdfConfiguration();
            const queryPageRequest: IQueryPageRequest = this.getExportingQueryPageRequest();

            const settings: PdfExportSettings = {
                source: PdfExportSource.WidgetGrid,
                queryPageRequest: queryPageRequest,
                configuration: configuration,
                fileName: this.widgetName
            };

            await this.widgetGridPdfExportingService.requestPdfExport(this.widgetSourceId, settings);

        } finally {
            this.pdfExportingLoading.next(false);
        }
    }

    // XLS exporting methods.
    public async generateXls(): Promise<void> {
        if (this.xlsExportingLoading.value) return;

        try {
            this.xlsExportingLoading.next(true);

            const queryPageRequest: IQueryPageRequest = this.getExportingQueryPageRequest();

            await this.widgetGridXlsExportingService.generateXls(this.widgetSourceId, this.widgetName, queryPageRequest);

        } finally {
            this.xlsExportingLoading.next(false);
        }
    }

    private async loadLinks(): Promise<void> {
        try {
            const linkDtos = await this.widgetGridViewService.getWidgetLinks(this._gridDisplayView.id);
            this.widgetLinks = linkDtos.map(l => ({ id: l.id, name: l.linkName, url: l.url }));
        } catch {
            this.widgetLinks = [];
        }
    }

    onActionMenuOpened(clickedRow: any): void {
        if (this.hasPatientId) {
            this.onGridRowSelect({ data: clickedRow });
        }
    }
}